import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, IconButton, Avatar, Chip, Typography } from "@mui/material";
import MemoryIcon from "@mui/icons-material/Memory";
import MenuIcon from "@mui/icons-material/Menu";

import { useAppSelector, useAppDispatch, toggleSidebar } from "../../store";
import { MAX_ACTIVE_INSTANCES } from "../../services";
import { APP_SHORT_NAME } from "../../constants";

// Map paths to breadcrumb labels
const PAGE_TITLES: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/services": "Services",
  "/instances": "Instances",
  "/settings": "Settings",
};

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const { activeCount } = useAppSelector((state) => state.instances);

  // Determine page title from path
  const basePath = "/" + location.pathname.split("/")[1];
  const pageTitle = PAGE_TITLES[basePath] || "";

  return (
    <Box
      sx={{
        height: 64,
        display: "flex",
        alignItems: "center",
        px: 3,
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        flexShrink: 0,
      }}
    >
      {/* Mobile menu button */}
      <IconButton
        onClick={() => dispatch(toggleSidebar())}
        sx={{ display: { md: "none" }, mr: 1, color: "text.primary" }}
      >
        <MenuIcon />
      </IconButton>
      {/* Page title */}
      {pageTitle && (
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: "text.primary",
          }}
        >
          {pageTitle}
        </Typography>
      )}
      <Box sx={{ flex: 1 }} />
      {isAuthenticated ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Chip
            icon={<MemoryIcon sx={{ fontSize: 16 }} />}
            label={`${activeCount} / ${MAX_ACTIVE_INSTANCES} instances`}
            size="small"
            sx={{
              bgcolor:
                activeCount >= MAX_ACTIVE_INSTANCES
                  ? "error.light"
                  : "action.hover",
              color:
                activeCount >= MAX_ACTIVE_INSTANCES
                  ? "error.dark"
                  : "text.secondary",
              fontWeight: 500,
              "& .MuiChip-icon": {
                color:
                  activeCount >= MAX_ACTIVE_INSTANCES
                    ? "error.dark"
                    : "text.secondary",
              },
            }}
          />

          <IconButton onClick={() => navigate("/settings")} size="small">
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: "primary.main",
                color: "primary.contrastText",
              }}
            >
              {user?.email?.charAt(0).toUpperCase() || "U"}
            </Avatar>
          </IconButton>
        </Box>
      ) : (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {APP_SHORT_NAME}
        </Typography>
      )}
    </Box>
  );
};

export default Header;
